// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import { Card, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import Calendario from "components/Calendario";
import dayjs from "dayjs";
import { ConfirmarContext } from "context/ConfirmarContext";
import ModalAddProducto from "./ModalAddProducto";
import ModalVerReceta from "../ModalVerReceta";
// import { ConfirmarContext } from "context/ConfirmarContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "90%", sm: "90%", xxl: "80%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalMenuDelDia({ open, handleClose }) {
  const [eventos, setEventos] = useState([]);
  const [modalAddProducto, setModalAddProducto] = useState(false);
  const [modalReceta, setOpenModalVerReceta] = useState(false);
  const [ingredientesList, setIngredientesList] = useState([]);
  const [prodSelected, setProdSelected] = useState({
    id: null,
    title: ""
  });

  const dateRef = useRef(null);
  const mesRef = useRef(Number(dayjs().format("M")));
  const anioRef = useRef(Number(dayjs().format("YYYY")));

  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const [deleteMenuDelDia] = useMutation(
    gql`
      mutation deleteMenuDelDia($id: ID!) {
        deleteMenuDelDia(id: $id) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Borrado correctamente", "success");
        handleRefetch({
          mes: mesRef.current,
          anio: anioRef.current,
        });
      },
    }
  );

  const { data: dataIngredientes, loading: loadingIngredientes } = useQuery(
    gql`
      query getIngredientes($id: ID!){
        ingredientesByProducto(id: $id){
              id
              cantidad
              materiaPrima {
                id
                nombre
                precio
                porcentajeUtilizable
                disabled
                tipoUnidad {
                  id
                  nombre
                  abreviatura
                  multiplicador
                }
              }
              tipoUnidad {
                id
                nombre
                abreviatura
                multiplicador
              }
        }
      }
    `,
    {
      variables: {
        id: prodSelected?.id
      },
      skip: !prodSelected?.id
    }
  )

  const calcularValoresIngrediente = (ingrediente) => {
    if (!ingrediente) return {
      valorUtilizable: 0,
      cantidadUtilizable: 0,
      precioUtilizable: 0,
      porcentajeUtilizable: 0
    };
    let cantidadIngresada = parseFloat(ingrediente.cantidad) || 0;
    let porcentajeUtilizable = parseFloat(ingrediente.materiaPrima?.porcentajeUtilizable) || 100;
    let precioPorUnidad = parseFloat(ingrediente.materiaPrima?.precio) || 0;

    let unidadCantidad = ingrediente.tipoUnidad;
    let unidadPrecio = ingrediente.tipoUnidadOrigen || unidadCantidad;

    let multiplicadorCantidad = parseFloat(unidadCantidad?.multiplicador) || 1;
    let multiplicadorPrecio =
      parseFloat(ingrediente?.materiaPrima?.tipoUnidad?.multiplicador) ||
      unidadPrecio?.multiplicador ||
      1;
    let cantidadNecesaria = cantidadIngresada / (porcentajeUtilizable / 100);
    let cantidadConvertida = (cantidadNecesaria * multiplicadorCantidad) / multiplicadorPrecio;
    let precioUtilizable = cantidadConvertida * precioPorUnidad;

    return {
      valorUtilizable: cantidadNecesaria,
      cantidadUtilizable: cantidadNecesaria,
      precioUtilizable: precioUtilizable,
      porcentajeUtilizable: porcentajeUtilizable
    };
  };

  const { refetch } = useQuery(
    gql`
      query getMenusDelMes($input: MenusDelMesInput) {
        menusDelMes(input: $input) {
          id
          idTurnoSede
          nombre
          precio
          limiteReservas
          limiteReservasSedeSP
          limiteReservasSedeTR
          limiteReservasSedeC
          fechaMenu
          turno
          producto {
            id
            nombre
            categoriaProducto {
              id
              icono
            }
          }
        }
      }
    `,
    {
      variables: {
        input: {
          mes: Number(dayjs().format("M")),
          anio: Number(dayjs().format("YYYY")),
        },
      },
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      onCompleted: (data) => {
        const eventosMapped = data.menusDelMes.filter((item) => item.producto).map((item) => ({
          id: item.id,
          idProducto: item.producto.id,
          date: item.fechaMenu,
          title: item.nombre,
          precio: item.precio,
          turno: item.turno,
          idTurnoSede: item.idTurnoSede,
          limiteReservas: item?.limiteReservas || 0,
          limiteReservasSedeSL: item?.limiteReservasSedeSL || 0,
          limiteReservasSedeSP: item?.limiteReservasSedeSP || 0,
          limiteReservasSedeTR: item?.limiteReservasSedeTR || 0,
          limiteReservasSedeC: item?.limiteReservasSedeC || 0,
          type: item.producto.categoriaProducto.id == 19 ? "Entrada" : "Menú",
          icon: item.producto.categoriaProducto.id == 19 ? item.producto.categoriaProducto.icono : "restaurant",
          color: item.producto.categoriaProducto.id == 19 ? "info" : "primary",
          actionList: [
            {
              tooltip: "Quitar de menú del día",
              btnColor: item.producto.categoriaProducto.id == 19 ? "info" : "primary",
              btnIcon: "cancel",
              action: () => {
                confirmar({
                  title: "Advertencia",
                  message: "¿Estás seguro de quitar este menú del día?",
                  icon: "warning",
                  func: () => {
                    deleteMenuDelDia({ variables: { id: item.id } })
                      .then(() => {
                        handleSnackbar("Borrado correctamente", "success");
                        refetch({
                          input: {
                            mes: mesRef.current,
                            anio: anioRef.current,
                          },
                        });
                      })
                      .catch(() => {
                        handleSnackbar("Ha ocurrido un error al borrar", "error");
                      });
                  },
                });
              }
            },
            {
              tooltip: "Ver receta",
              btnColor: "dark",
              btnIcon: "visibility",
              action: () => {setProdSelected({id: Number(item?.producto?.id), title: item?.producto?.nombre}); setOpenModalVerReceta(true);}
            },
            {
              tooltip: "Editar límite",
              btnColor: "info",
              btnIcon: "edit",
              action: () => {
                // Esta acción será manejada por el componente EventosCard
              }
            },
          ],
        }));
        setEventos(eventosMapped);
      },
    }
  );

  useEffect(() => {
    if (dataIngredientes) {
      let listadoExistente = [];

      listadoExistente = dataIngredientes?.ingredientesByProducto?.map((ingrediente) => {
        let valoresCalculados = calcularValoresIngrediente(ingrediente);

        return {
          cantidad: ingrediente.cantidad,
          nombre: ingrediente.materiaPrima.nombre,
          idTipoUnidad: ingrediente.tipoUnidad.id,
          idMateriaPrima: ingrediente.materiaPrima.id,
          tipoUnidad: ingrediente.tipoUnidad,
          tipoUnidadOrigen: ingrediente.materiaPrima?.tipoUnidad,
          precio: ingrediente.materiaPrima?.precio,
          idExistente: ingrediente.id,
          disabled: ingrediente.materiaPrima.disabled,
          ...valoresCalculados,
        };
      });
      setIngredientesList(listadoExistente)
    }
  }, [dataIngredientes])

  const handleRefetch = ({ mes, anio }) => {
    mesRef.current = mes;
    anioRef.current = anio;
    refetch({
      input: {
        mes: mes,
        anio: anio,
      },
    }).then((result) => {
      const eventosMapped = result.data.menusDelMes.filter((item) => item.producto).map((item) => ({
        id: item.id,
        idProducto: item.producto.id,
        date: item.fechaMenu,
        title: item.nombre,
        precio: item.precio,
        turno: item.turno,
        idTurnoSede: item.idTurnoSede,
        limiteReservas: item?.limiteReservas || 0,
        limiteReservasSedeSL: item?.limiteReservasSedeSL || 0,
        limiteReservasSedeSP: item?.limiteReservasSedeSP || 0,
        limiteReservasSedeTR: item?.limiteReservasSedeTR || 0,
        limiteReservasSedeC: item?.limiteReservasSedeC || 0,
        type: item.producto.categoriaProducto.id == 19 ? "Entrada" : "Menú",
        icon: item.producto.categoriaProducto.id == 19 ? item.producto.categoriaProducto.icono : "restaurant",
        color: item.producto.categoriaProducto.id == 19 ? "info" : "primary",
        actionList: [
          {
            tooltip: "Quitar de menú del día",
            btnColor: item.producto.categoriaProducto.id == 19 ? "info" : "primary",
            btnIcon: "cancel",
            action: () => {
              confirmar({
                title: "Advertencia",
                message: "¿Estás seguro de quitar este menú del día?",
                icon: "warning",
                func: () => {
                  deleteMenuDelDia({ variables: { id: item.id } })
                    .then(() => {
                      handleSnackbar("Borrado correctamente", "success");
                      refetch({
                        input: {
                          mes: mesRef.current,
                          anio: anioRef.current,
                        },
                      });
                    })
                    .catch(() => {
                      handleSnackbar("Ha ocurrido un error al borrar", "error");
                    });
                },
              });
            }
          },
          {
            tooltip: "Ver receta",
            btnColor: "dark",
            btnIcon: "visibility",
            action: () => {setProdSelected({id: Number(item?.producto?.id), title: item?.producto?.nombre}); setOpenModalVerReceta(true);}
          },
          {
            tooltip: "Editar límite",
            btnColor: "info",
            btnIcon: "edit",
            action: () => {
              // Esta acción será manejada por el componente EventosCard
            }
          },
        ],
      }));
      setEventos(eventosMapped);
    });
  };

  useEffect(() => {
    if (open) {
      refetch({
        input: {
          mes: Number(dayjs().format("M")),
          anio: Number(dayjs().format("YYYY")),
        },
      });
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Menú del día</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={2}>
              <Grid container spacing={2} pb={3}>
                <Calendario
                  events={eventos}
                  onMonthChange={(month) => {
                    handleRefetch({
                      mes: Number(month + 1),
                      anio: anioRef.current,
                    });
                  }}
                  onYearChange={(year) => {
                    handleRefetch({
                      mes: mesRef.current,
                      anio: Number(year),
                    });
                  }}
                  onDateSelect={(date) => {
                    dateRef.current = date;
                  }}
                  onClickButton={() => setModalAddProducto(true)}
                  onRefetch={() => {
                    handleRefetch({
                      mes: mesRef.current,
                      anio: anioRef.current,
                    });
                  }}
                />
              </Grid>
            </SoftBox>
          </SoftBox>
          <ModalAddProducto
            open={modalAddProducto}
            handleClose={() => setModalAddProducto(false)}
            refetch={() => {
              handleRefetch({
                mes: mesRef.current,
                anio: anioRef.current,
              });
            }}
            fecha={dateRef.current}
          />
          <ModalVerReceta open={modalReceta} title={prodSelected?.title} onClose={() => setOpenModalVerReceta(false)} ingredientesList={ingredientesList} loadingList={loadingIngredientes} />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalMenuDelDia.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
